import { useMemo, useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import SEO, { breadcrumbSchema } from "../components/SEO";
import PageHero from "../components/PageHero";
import CtaBand from "../components/CtaBand";
import { GALLERY, GALLERY_CATS } from "../data/content";
import { img, IMAGES } from "../data/site";

export default function Gallery() {
  const [cat, setCat] = useState("All");
  const [open, setOpen] = useState(null);
  const items = useMemo(() => GALLERY.filter((g) => cat === "All" || g.cat === cat), [cat]);

  const nav = useCallback((d) => setOpen((o) => (o === null ? null : (o + d + items.length) % items.length)), [items.length]);
  useEffect(() => {
    if (open === null) return;
    const h = (e) => {
      if (e.key === "Escape") setOpen(null);
      if (e.key === "ArrowRight") nav(1);
      if (e.key === "ArrowLeft") nav(-1);
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [open, nav]);

  return (
    <>
      <SEO
        title="Gallery — Rajasthan & Our Taxis"
        description="Photos from the road: Jodhpur's blue lanes, Rajasthan's forts, lakes and desert, and the clean Blue City Taxi fleet that takes you there."
        path="/gallery"
        schemas={[breadcrumbSchema([{ name: "Home", path: "/" }, { name: "Gallery", path: "/gallery" }])]}
      />
      <PageHero
        title="Rajasthan Through Our Windshield"
        subtitle="Forts, lakes, dunes, highways and the cars that connect them — a glimpse of where a Blue City Taxi journey takes you."
        image={IMAGES.camels}
        crumbs={[{ label: "Home", to: "/" }, { label: "Gallery" }]}
      />
      <section className="max-w-7xl mx-auto px-6 py-14">
        <div className="flex gap-2 overflow-x-auto no-scrollbar pb-2 -mx-1 px-1" data-testid="gallery-filters">
          {GALLERY_CATS.map((c) => (
            <button
              key={c}
              onClick={() => { setCat(c); setOpen(null); }}
              data-testid={`gallery-filter-${c.toLowerCase().replace(/\s+/g, "-")}`}
              className={`shrink-0 rounded-full px-4 py-2 text-xs font-bold transition-colors ${
                cat === c ? "bg-royal text-white" : "bg-white border border-slate-200 text-slate-600 hover:border-royal/40 hover:text-royal"
              }`}
            >
              {c}
            </button>
          ))}
        </div>
        <div className="mt-8 columns-2 md:columns-3 xl:columns-4 gap-4 [column-fill:balance]" data-testid="gallery-grid">
          {items.map((g, i) => (
            <motion.button
              key={g.src + g.alt}
              initial={{ opacity: 0, y: 18 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: (i % 4) * 0.05 }}
              onClick={() => setOpen(i)}
              data-testid={`gallery-item-${i}`}
              className="group relative mb-4 w-full overflow-hidden rounded-2xl break-inside-avoid focus:outline-none focus:ring-2 focus:ring-royal"
            >
              <img
                src={img(g.src, 700)}
                alt={g.alt}
                loading="lazy"
                className="w-full group-hover:scale-105 transition-transform duration-700"
              />
              <span className="absolute bottom-2 left-2 rounded-full bg-navy/70 backdrop-blur px-2.5 py-1 text-[10px] font-bold text-white/90 opacity-0 group-hover:opacity-100 transition-opacity">
                {g.cat}
              </span>
            </motion.button>
          ))}
        </div>
      </section>

      <AnimatePresence>
        {open !== null && items[open] && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] bg-navy/95 backdrop-blur flex items-center justify-center p-4"
            onClick={() => setOpen(null)}
            data-testid="gallery-lightbox"
          >
            <button aria-label="Close" data-testid="lightbox-close" className="absolute top-4 right-4 text-white/70 hover:text-gold p-2" onClick={() => setOpen(null)}>
              <X size={26} />
            </button>
            <button aria-label="Previous" data-testid="lightbox-prev" className="absolute left-3 sm:left-6 text-white/70 hover:text-gold p-2" onClick={(e) => { e.stopPropagation(); nav(-1); }}>
              <ChevronLeft size={34} />
            </button>
            <motion.figure
              key={items[open].src}
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              className="max-w-4xl max-h-[82vh]"
              onClick={(e) => e.stopPropagation()}
            >
              <img src={img(items[open].src, 1400)} alt={items[open].alt} className="max-h-[74vh] w-auto mx-auto rounded-xl shadow-2xl" />
              <figcaption className="mt-3 text-center text-sm text-white/70">{items[open].alt}</figcaption>
            </motion.figure>
            <button aria-label="Next" data-testid="lightbox-next" className="absolute right-3 sm:right-6 text-white/70 hover:text-gold p-2" onClick={(e) => { e.stopPropagation(); nav(1); }}>
              <ChevronRight size={34} />
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      <CtaBand title="Like what you see? Come see it in person." />
    </>
  );
}
